import { coerceBooleanProperty, coerceNumberProperty } from '@angular/cdk/coercion';
import { NgClass } from '@angular/common';
import { Component, Input, input, output } from '@angular/core';
import { NG_VALUE_ACCESSOR } from '@angular/forms';
import { DxcCheckboxModule, ThemeModule } from '@dxc-technology/halstack-angular';
import { CheckboxProperties } from '@dxc-technology/halstack-angular/lib/dxc-checkbox/dxc-checkbox.types';
import { TranslateModule } from '@ngx-translate/core';
import { RequiredControlValueAccessor } from '@shared/interface/common.interface';

@Component({
  selector: 'custom-checkbox',
  templateUrl: './custom-checkbox.component.html',
  styleUrls: ['./custom-checkbox.component.scss'],
  imports: [ThemeModule, DxcCheckboxModule, TranslateModule, NgClass],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: CustomCheckboxComponent,
      multi: true,
    },
  ],
})
export class CustomCheckboxComponent implements RequiredControlValueAccessor {
  /**
   * If true, the component is checked. If undefined the component will be uncontrolled
   * and the value will be managed internally by the component.
   */
  // TODO: Skipped for migration because:
  //  Accessor inputs cannot be migrated as they are too complex.
  @Input()
  get checked(): boolean {
    return this._checked;
  }
  set checked(value: boolean) {
    this._checked = coerceBooleanProperty(value);
  }
  private _checked;
  @Input() error: string = '';
  /**
   * Will be passed to the value attribute of the html input element. When inside a form,
   * this value will be only submitted if the checkbox is checked.
   */
  readonly value = input<string>('');

  /**
   * Text to be placed next to the checkbox.
   */
  readonly label = input<string>(undefined);

  /**
   * Whether the label should appear after or before the checkbox.
   */
  readonly labelPosition = input<CheckboxProperties['labelPosition']>('before');

  /**
   * Name attribute of the input element.
   */
  readonly name = input<string>(undefined);

  /**
   * If true, the component will be disabled.
   */
  // TODO: Skipped for migration because:
  //  Accessor inputs cannot be migrated as they are too complex.
  @Input()
  get disabled(): boolean {
    return this._disabled;
  }
  set disabled(value: boolean) {
    this._disabled = coerceBooleanProperty(value);
  }
  private _disabled = false;

  /**
   * If true, the checkbox will change its appearence, showing that the value is required.
   */
  // TODO: Skipped for migration because:
  //  Accessor inputs cannot be migrated as they are too complex.
  @Input()
  get required(): boolean {
    return this._required;
  }
  set required(value: boolean) {
    this._required = coerceBooleanProperty(value);
  }
  private _required = false;

  /**
   * Value of the tabindex attribute.
   */
  // TODO: Skipped for migration because:
  //  Accessor inputs cannot be migrated as they are too complex.
  @Input()
  get tabIndexValue(): number {
    return this._tabIndexValue;
  }
  set tabIndexValue(value: number) {
    this._tabIndexValue = coerceNumberProperty(value);
  }
  private _tabIndexValue = 0;

  /**
   * This event will emit in case the user clicks the checkbox.
   * The new value will be passed as a parameter.
   */
  readonly onChange = output<boolean>();

  /**
   * Size of the margin to be applied to the component
   * ('xxsmall' | 'xsmall' | 'small' | 'medium' | 'large' | 'xlarge' | 'xxlarge').
   * You can pass an object with 'top', 'bottom', 'left' and 'right' properties
   * in order to specify different margin sizes.
   */
  readonly margin = input<CheckboxProperties['margin']>(undefined);

  /**
   * Size of the component.
   */
  readonly size = input<CheckboxProperties['size']>('fitContent');

  readonly id = input<string>(undefined);

  onChangeHandler(event: boolean) {
    this.onChange.emit(event);
    this.onChangeForm?.(event);
    this.writeValue?.(event);
  }

  onChangeForm: (value: any) => void;
  onTouched: () => void;

  writeValue(value: any): void {
    this.checked = value;
  }

  registerOnChange(fn: any): void {
    this.onChangeForm = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }
}
